import api from "@/services/api";
import { SelectionTemporalDataFilter, TimeUnit } from "@/services/data_filters";
import { RecommendationType } from "@/services/recommendations/recommendations";
import { RecommendationTask } from "@/services/recommendations/tasks";
import { useDataFilters } from "@/store/data_filters";
import { computed, reactive, ComputedRef } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { DateTime } from "luxon";
import { useAnnotationsStore } from "@/store/annotations";
import { QueryFunctionContext } from "@/utils/typing";
import { useAuthStore } from "@/store/auth";
import {
  usePublicDashboard,
  usePublicDashboardQueryFilters,
} from "@/services/public_dashboard";
import {
  type ChannelDetail,
  channelDetails,
  ResourceChannel,
} from "@/services/resources";

export interface BaseAnnotation {
  start: string;
  end: string | null;
}

export interface BaseStoredAnnotation extends BaseAnnotation {
  pk: number;
  organization: number;
  duration: EventDuration;
}

export interface UserAnnotation extends BaseStoredAnnotation {
  description: string;
  channel: AnnotationChannel.USER;
  type: UserEventType;
  feedItem?: number | null;
  user: { id: number; name: string; profilePictureUrl?: string | null } | null;
}

export interface UserAnnotationPayload extends BaseAnnotation {
  description: string;
  type: UserEventType;
  feedItem?: number | null;
}

// Annotation types

export enum AutomatedAnnotationType {
  CAMPAIGN_STATUS_CHANGED = "Campaign status changed",
  CHANNEL_TRAFFIC_INCREASE = "Channel traffic increase",
  ORGANIC_POST_PUBLISHED = "Organic post published",
  METRIC_DEFINITION_CHANGED = "Metric definition changed",
}

export enum EventDuration {
  SINGLE_DAY = "Single-day event",
  MULTI_DAY = "Multi-day event",
  CONTINUOUS = "Ongoing event",
}

export enum AnnotationChannel {
  // The "root" category of an Annotation - the first 5 are created by K8
  FACEBOOK_ADS = "Facebook Ads",
  FACEBOOK_ORGANIC = "Facebook Organic",
  INSTAGRAM_ORGANIC = "Instagram Organic",
  GOOGLE_ADS = "Google Ads",
  GOOGLE_ANALYTICS = "Google Analytics",
  GOOGLE_ANALYTICS_4 = "Google Analytics 4",
  GOOGLE_BIG_QUERY = "Google BigQuery",
  GOOGLE_BIG_QUERY_ANALYTICS = "Google BigQuery Analytics",
  YOUTUBE = "YouTube",
  MERCHANT_CENTER = "Merchant Center",
  LINKED_IN_ADS = "LinkedIn Ads",
  LINKED_IN_ORGANIC = "LinkedIn Organic",
  SNAPCHAT_ADS = "Snapchat Ads",
  XANDR = "Xandr",
  BING_ADS = "Bing Ads",
  TIK_TOK_ADS = "TikTok Ads",
  TIK_TOK_ORGANIC = "TikTok Organic",
  USER = "User added event",
}

export type AutomatedAnnotationChannel = Exclude<
  AnnotationChannel,
  AnnotationChannel.USER
>;

// Every channel above, so a new one has to be added here too.
const ANNOTATION_RESOURCE_CHANNELS: Record<
  AutomatedAnnotationChannel,
  ResourceChannel
> = {
  [AnnotationChannel.FACEBOOK_ADS]: ResourceChannel.FACEBOOK_ADS,
  [AnnotationChannel.FACEBOOK_ORGANIC]: ResourceChannel.FACEBOOK_ORGANIC,
  [AnnotationChannel.INSTAGRAM_ORGANIC]: ResourceChannel.INSTAGRAM_ORGANIC,
  [AnnotationChannel.GOOGLE_ADS]: ResourceChannel.GOOGLE_ADS,
  [AnnotationChannel.GOOGLE_ANALYTICS]: ResourceChannel.GOOGLE_ANALYTICS,
  [AnnotationChannel.GOOGLE_ANALYTICS_4]: ResourceChannel.GOOGLE_ANALYTICS_4,
  [AnnotationChannel.GOOGLE_BIG_QUERY]: ResourceChannel.GOOGLE_BIG_QUERY,
  [AnnotationChannel.GOOGLE_BIG_QUERY_ANALYTICS]:
    ResourceChannel.GOOGLE_BIG_QUERY_ANALYTICS,
  [AnnotationChannel.YOUTUBE]: ResourceChannel.YOUTUBE,
  [AnnotationChannel.MERCHANT_CENTER]: ResourceChannel.MERCHANT_CENTER,
  [AnnotationChannel.LINKED_IN_ADS]: ResourceChannel.LINKED_IN_ADS,
  [AnnotationChannel.LINKED_IN_ORGANIC]: ResourceChannel.LINKED_IN_ORGANIC,
  [AnnotationChannel.SNAPCHAT_ADS]: ResourceChannel.SNAPCHAT_ADS,
  [AnnotationChannel.XANDR]: ResourceChannel.XANDR,
  [AnnotationChannel.BING_ADS]: ResourceChannel.BING_ADS,
  [AnnotationChannel.TIK_TOK_ADS]: ResourceChannel.TIK_TOK_ADS,
  [AnnotationChannel.TIK_TOK_ORGANIC]: ResourceChannel.TIK_TOK_ORGANIC,
};

export function annotationChannelDetail(
  channel: AutomatedAnnotationChannel,
): ChannelDetail {
  return channelDetails[ANNOTATION_RESOURCE_CHANNELS[channel]];
}

export enum UserEventType {
  COMPETITOR = "Competitor",
  GLOBAL_EVENT = "Global event",
  LOCAL_EVENT = "Local event",
  NEWSLETTER = "Newsletter",
  NEWSPAPER_MENTION = "Newspaper mention",
  PHYSICAL_PROMOTION = "Physical promotion",
  RADIO_MENTION = "Radio mention",
  SOCIAL_MEDIA_COMPETITION = "Social media competition",
  DISPLAY_MARKETING = "Display marketing",
  TV_MENTION = "TV mention",
  WEBSITE_UPDATE = "Website update",
  CAMPAIGN_CHANGES = "Campaign changes",
  OTHER = "Other event",
}

// Automated annotation details

export interface CampaignMetricChangedAnnotationDetails {
  campaignId: string;
  campaignName: string;
  change: number;
  direction: "increase" | "decrease";
  metric: string;
}

export interface CampaignStatusChangedAnnotationDetails {
  campaignId: string;
  campaignName: string;
  isActive: boolean;
}

// The backend stores a channel and a type independently (its seed data pairs
// every channel with every type), so a type doesn't narrow the channel.
interface BaseAutomatedAnnotation extends BaseStoredAnnotation {
  channel: AutomatedAnnotationChannel;
}

export interface CampaignStatusChangedAnnotation extends BaseAutomatedAnnotation {
  type: AutomatedAnnotationType.CAMPAIGN_STATUS_CHANGED;
  details: CampaignStatusChangedAnnotationDetails;
}

export interface RecommendationTaskVerifiedAnnotationDetails {
  recommendationTaskId: number;
  recommendationTaskRecommendationName: RecommendationType;
  recommendationTaskExternalId: string;
  recommendationTaskDetails: RecommendationTask["details"];
  userId: number | null;
}

export interface ChannelTrafficIncreaseAnnotationDetails {
  channel: string;
  users: number;
  previousUsers: number;
}

export interface ChannelTrafficIncreaseAnnotation extends BaseAutomatedAnnotation {
  type: AutomatedAnnotationType.CHANNEL_TRAFFIC_INCREASE;
  details: ChannelTrafficIncreaseAnnotationDetails;
}

export interface OrganicPostPublishedAnnotation extends BaseAutomatedAnnotation {
  type: AutomatedAnnotationType.ORGANIC_POST_PUBLISHED;
  details: Record<string, never>;
  post: number | null;
}

export interface MetricDefinitionChangedAnnotation extends BaseAutomatedAnnotation {
  type: AutomatedAnnotationType.METRIC_DEFINITION_CHANGED;
  details: Record<string, never>;
}

export type AutomatedAnnotation =
  | CampaignStatusChangedAnnotation
  | ChannelTrafficIncreaseAnnotation
  | OrganicPostPublishedAnnotation
  | MetricDefinitionChangedAnnotation;

export type Annotation = AutomatedAnnotation | UserAnnotation;

// Api consumers

function createAnnotation(
  annotation: UserAnnotationPayload,
): Promise<UserAnnotation> {
  return api
    .post("/annotations/user_annotations/", annotation)
    .then((response) => response.data);
}

export function useCreateAnnotationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: createAnnotation,
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ["annotations"] });
        queryClient.invalidateQueries({
          queryKey: ["annotationCountsPerDate"],
        });
      },
    }),
  );
}

export const LIST_USER_ANNOTATIONS_URL = "/annotations/user_annotations/";

export function listUserAnnotations(
  params: SelectionTemporalDataFilter,
): Promise<UserAnnotation[]> {
  const eventTypes = new Set(Object.values(UserEventType));
  return api
    .get<UserAnnotation[]>(LIST_USER_ANNOTATIONS_URL, { params })
    .then((response) => {
      return response.data.filter((annotation) =>
        eventTypes.has(annotation.type),
      );
    });
}

export const LIST_AUTOMATED_ANNOTATIONS_URL =
  "/annotations/automated_annotations/";

export function listAutomatedAnnotations(
  params: SelectionTemporalDataFilter,
): Promise<AutomatedAnnotation[]> {
  const annotationTypes = new Set(Object.values(AutomatedAnnotationType));
  const annotationChannels = new Set(Object.values(AnnotationChannel));
  return api
    .get<AutomatedAnnotation[]>(LIST_AUTOMATED_ANNOTATIONS_URL, {
      params,
    })
    .then((response) => {
      return response.data.filter(
        (annotation) =>
          annotationTypes.has(annotation.type) &&
          annotationChannels.has(annotation.channel),
      );
    });
}

function listAnnotations({
  queryKey: [, params],
}: QueryFunctionContext<SelectionTemporalDataFilter>): Promise<Annotation[]> {
  return Promise.all([
    listUserAnnotations(params),
    listAutomatedAnnotations(params),
  ]).then(([userAnnotations, automatedAnnotations]) => [
    ...userAnnotations,
    ...automatedAnnotations,
  ]);
}

export function useAnnotationsQuery() {
  const temporalFilter = useDataFilters(["selectionTemporalFilter"]);
  const publicDashboard = usePublicDashboard();
  const dataFilters = {
    ...usePublicDashboardQueryFilters(),
    ...temporalFilter,
    // We ensure that if the user selects a date range that ends yesterday or later,
    // we also include today's and future annotations.
    selection_to: computed(() =>
      DateTime.fromISO(temporalFilter.selection_to.value) >=
      DateTime.local().minus({ days: 1 }).startOf("day")
        ? null
        : temporalFilter.selection_to.value,
    ),
  };
  const queryKey = ["annotations", dataFilters] as const;
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey,
      queryFn: listAnnotations,
      enabled: computed(() =>
        publicDashboard
          ? (publicDashboard.value?.shareAnnotations ?? false)
          : authStore.isAuthed,
      ),
    }),
  );
}

export function useAnnotationsForClickedDate(): ComputedRef<Annotation[]> {
  const annotationsStore = useAnnotationsStore();
  const annotations = useAnnotationsQuery();
  return computed(() => {
    return (
      annotations.data?.filter((e) => {
        const timeUnit = annotationsStore.timeUnit;
        return (
          timeUnit &&
          timeUnit !== TimeUnit.WEEKDAY &&
          DateTime.fromISO(e.start).startOf(timeUnit).toISODate() ===
            annotationsStore.clickedDate
        );
      }) ?? []
    );
  });
}

function deleteUserAnnotation(pk: number): Promise<void> {
  return api
    .delete(`/annotations/user_annotations/${pk}/`)
    .then((response) => response.data);
}

export function useDeleteUserAnnotationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: deleteUserAnnotation,
      onSuccess: (_, pk) => {
        queryClient.setQueriesData<Annotation[]>(
          { queryKey: ["annotations"] },
          (annotations) =>
            annotations?.filter(
              (annotation: Annotation) =>
                annotation.channel !== AnnotationChannel.USER ||
                annotation.pk !== pk,
            ),
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["annotationCountsPerDate"],
        });
      },
    }),
  );
}

function deleteAutomatedAnnotation(pk: number): Promise<void> {
  return api
    .delete(`/annotations/automated_annotations/${pk}/`)
    .then((response) => response.data);
}

export function useDeleteAutomatedAnnotationMutation() {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: deleteAutomatedAnnotation,
      onSuccess: (_, pk) => {
        queryClient.setQueriesData<Annotation[]>(
          { queryKey: ["annotations"] },
          (annotations) =>
            annotations?.filter(
              (annotation: Annotation) =>
                annotation.channel === AnnotationChannel.USER ||
                annotation.pk !== pk,
            ),
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["annotationCountsPerDate"],
        });
      },
    }),
  );
}

function getAnnotationCountsPerDate({
  queryKey: [, params],
}: QueryFunctionContext<SelectionTemporalDataFilter>): Promise<
  Record<string, number>
> {
  return api
    .get("/annotations/annotation_counts_per_day/", {
      params,
    })
    .then((response) => response.data);
}

export function useAnnotationCountsPerDateQuery() {
  const publicDashboard = usePublicDashboard();
  const queryKey = [
    "annotationCountsPerDate",
    {
      ...usePublicDashboardQueryFilters(),
      ...useDataFilters(["selectionTemporalFilter"]),
    },
  ] as const;
  const authStore = useAuthStore();
  return reactive(
    useQuery({
      queryKey,
      queryFn: getAnnotationCountsPerDate,
      enabled: computed(() =>
        publicDashboard
          ? (publicDashboard.value?.shareAnnotations ?? false)
          : authStore.isAuthed,
      ),
    }),
  );
}
